<template>
    <!-- Control only: the label and the status icon are the property row's
         columns (see PropertyRow.vue). border_side is a `wide` row, so this
         group gets its own full-width line on a narrow sidebar; flex-wrap is
         the last resort below that, so no button is ever clipped. -->
    <div class="flex flex-wrap items-center gap-0.5">
        <!-- All / None are ACTIONS on the four toggles, not toggles themselves:
             they never render a pressed state, they just write FULL / NONE. -->
        <button v-for="action in actions" :key="action.key"
            type="button"
            class="px-1 py-0.5 rounded border border-transparent bg-slate-700/60 text-slate-300 hover:bg-slate-600 focus:outline-none shrink-0"
            :title="action.title"
            :aria-label="action.title"
            @click="$emit('update:modelValue', action.value)">
            <svg viewBox="0 0 16 16" class="w-4 h-4" aria-hidden="true">
                <rect x="2.5" y="2.5" width="11" height="11" fill="none"
                    stroke="currentColor" stroke-opacity="0.3" stroke-width="1" />
                <line v-for="(edge, i) in action.edges" :key="i"
                    :x1="edge[0]" :y1="edge[1]" :x2="edge[2]" :y2="edge[3]"
                    stroke="currentColor" stroke-width="2.5" stroke-linecap="square" />
            </svg>
        </button>

        <span class="w-px h-4 bg-slate-600/60 mx-0.5 shrink-0" aria-hidden="true"></span>

        <!-- The four side toggles are the source of truth for the stored bitmask. -->
        <button v-for="side in sides" :key="side.key"
            type="button"
            class="px-1 py-0.5 rounded border focus:outline-none shrink-0"
            :class="isOn(side.bit)
                ? 'bg-blue-600 border-blue-500 text-white hover:bg-blue-500'
                : 'bg-slate-600/60 border-transparent text-slate-300 hover:bg-slate-500/60'"
            :title="side.title"
            :aria-label="side.title"
            :aria-pressed="isOn(side.bit)"
            @click="toggle(side.bit)">
            <svg viewBox="0 0 16 16" class="w-4 h-4" aria-hidden="true">
                <rect x="2.5" y="2.5" width="11" height="11" fill="none"
                    stroke="currentColor" stroke-opacity="0.3" stroke-width="1" />
                <line :x1="side.edge[0]" :y1="side.edge[1]" :x2="side.edge[2]" :y2="side.edge[3]"
                    stroke="currentColor" stroke-width="2.5" stroke-linecap="square" />
            </svg>
        </button>
    </div>
</template>

<script>
// Reusable editor for the LVGL border_side bitmask. It stores and reads ONLY
// the numeric bitmask — no schema, import/export or StyleResolver involvement —
// so it drops into PartPropertiesPanel wherever that value is edited, for the
// default state and for per-state overrides alike.
//
// The bit constants come from the geometry module that renders them
// (src/draw/borderGeometry.ts) rather than being restated here: one registry.
import {
    BORDER_SIDE_NONE,
    BORDER_SIDE_BOTTOM,
    BORDER_SIDE_TOP,
    BORDER_SIDE_LEFT,
    BORDER_SIDE_RIGHT,
    BORDER_SIDE_FULL,
} from '../../draw/borderGeometry';

// Edge segments in the icons' 16x16 viewBox, drawn over a faint full square.
const EDGE = {
    top:    [2, 2.5, 14, 2.5],
    bottom: [2, 13.5, 14, 13.5],
    left:   [2.5, 2, 2.5, 14],
    right:  [13.5, 2, 13.5, 14],
};

export default {
    name: 'BorderSideEditor',

    props: {
        // The raw LVGL bitmask. undefined/null means "not set", which renders
        // as all four sides — the same fallback the renderer applies (see
        // borderGeometry.ts / lv_draw_rect_dsc_init).
        modelValue: { type: [Number, String], default: undefined },
    },

    emits: ['update:modelValue'],

    computed: {
        actions() {
            return [
                { key: 'all',  title: 'All sides',  value: BORDER_SIDE_FULL, edges: Object.values(EDGE) },
                { key: 'none', title: 'No sides',   value: BORDER_SIDE_NONE, edges: [] },
            ];
        },

        // Row order is fixed: top, right, bottom, left.
        sides() {
            return [
                { key: 'top',    title: 'Top border',    bit: BORDER_SIDE_TOP,    edge: EDGE.top },
                { key: 'right',  title: 'Right border',  bit: BORDER_SIDE_RIGHT,  edge: EDGE.right },
                { key: 'bottom', title: 'Bottom border', bit: BORDER_SIDE_BOTTOM, edge: EDGE.bottom },
                { key: 'left',   title: 'Left border',   bit: BORDER_SIDE_LEFT,   edge: EDGE.left },
            ];
        },

        // Initialise the toggles from whatever numeric value is stored —
        // 15 -> all on, 0 -> all off, 7 -> top+bottom+left, 8 -> right only.
        // Masked to the four side bits so a stray high bit can't light a toggle.
        value() {
            if (this.modelValue === undefined || this.modelValue === null || this.modelValue === '') {
                return BORDER_SIDE_FULL;
            }
            const n = Number(this.modelValue);
            return Number.isFinite(n) ? (n & BORDER_SIDE_FULL) : BORDER_SIDE_FULL;
        },
    },

    methods: {
        isOn(bit) {
            return (this.value & bit) !== 0;
        },

        // The stored value is always recomputed from the four toggles:
        //   (bottom ? 1 : 0) | (top ? 2 : 0) | (left ? 4 : 0) | (right ? 8 : 0)
        toggle(bit) {
            this.$emit('update:modelValue', this.value ^ bit);
        },
    },
};
</script>
